import { useGetOrganizationList } from '@forwardflow/hooks/customHooks'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'
import { useAppStore } from '@forwardflow/store'
import { Spinner } from '@forwardflow/ui'
import { UserAvatar } from '@forwardflow/ui/Avatar'
import { message, Select } from 'antd'
import { useEffect, useState, useMemo } from 'react'
import { SearchField } from '@forwardflow/ui/SearchField'

type OnboardingType = {
   children?: React.ReactNode
   avatar?: boolean
   className?: string
}

const OnboardingContainer: React.FC<OnboardingType> = ({
   children,
   avatar = true,
   className,
}) => {
   const LIMIT = 100
   const { setCurrentOrganization } = useAppStore()
   const { data: user } = useAuthUser({ revalidateOnMount: true })
   const [hasMore, setHasMore] = useState(true)
   const [isLoadingMore, setIsLoadingMore] = useState(false)
   const [searchValue, setSearchValue] = useState('')
   const [organizationParams, setOrganizationParams] = useState({
      page: 1,
      limit: LIMIT,
   })
   const { currentOrganization } = useAppStore()
   const selectedOrganization = currentOrganization
   const { navigateUser } = useUserNavigation()
   const shouldFetch =
      user?.email === 'operator@forward-flow.com' ||
      user?.email === 'brannon@forward-flow.com' ||
      user?.email === 'engineered@forward-flow.com'

   const onSelectOrganization = (value: string) => {
      try {
         setCurrentOrganization(value)
         message.success('Organization changed successfully')
         navigateUser({
            currentOrgId: value,
            searchParams: location.search,
         })
      } catch (error) {
         console.log('error', error)
      }
   }

   const { data: organizationList, isLoading } = useGetOrganizationList({
      ...organizationParams,
      shouldFetch,
   })

   const loadMoreOrganizations = () => {
      if (!isLoadingMore && !isLoading && hasMore) {
         setIsLoadingMore(true)
         setOrganizationParams((prev) => ({
            ...prev,
            page: prev.page + 1,
         }))
      }
   }

   const [allOrganizations, setAllOrganizations] = useState<any[]>([])

   useEffect(() => {
      if (organizationList && organizationList.length > 0) {
         setAllOrganizations((prev) => {
            const combined = [...prev, ...organizationList]

            return Array.from(
               new Map(combined.map((item) => [item.id, item])).values()
            )
         })
         if (organizationList.length < LIMIT) {
            setHasMore(false)
         }
         setIsLoadingMore(false)
      }
   }, [organizationList])

   const organizationOptions = allOrganizations?.map((item) => {
      return {
         value: item.id,
         label: item.organizationName + ' - ' + item.id,
      }
   })

   const filteredOptions = useMemo(() => {
      if (!searchValue) return organizationOptions
      return organizationOptions.filter((option) =>
         option.label.toLowerCase().includes(searchValue.toLowerCase())
      )
   }, [organizationOptions, searchValue])

   // Auto-select first organization if none is selected
   useEffect(() => {
      if (
         !selectedOrganization &&
         organizationOptions &&
         organizationOptions.length > 0
      ) {
         onSelectOrganization(organizationOptions[0].value)
      }
   }, [organizationOptions, selectedOrganization])

   // Show spinner if no organization is selected
   if (!selectedOrganization && shouldFetch) {
      return (
         <div className="flex items-center justify-center min-h-screen">
            <Spinner />
         </div>
      )
   }

   return (
      <>
         <div
            className={`flex min-h-screen px-4 lg:px-[30px] flex-col  items-center text-center pt-14 md:pt-28 ${className}`}
         >
            {avatar && <UserAvatar />}
            <a href="/">
               <img
                  width={'165px'}
                  src="/images/fff-logo.png"
                  className="md:absolute left-10 top-10"
                  alt="ff icon"
               />
            </a>
            <div className="absolute top-10 right-10">
               {shouldFetch && (
                  <Select
                     className="ml-[10px] w-[120px] lg:w-[200px] dashboard-select"
                     placeholder="Select Organization"
                     options={filteredOptions}
                     onChange={onSelectOrganization}
                     value={selectedOrganization || ''}
                     loading={isLoading || isLoadingMore}
                     notFoundContent={<div className="hidden" />}
                     onPopupScroll={(e) => {
                        const target = e.target as HTMLDivElement
                        const threshold = 50
                        const isNearBottom =
                           target.scrollTop + target.offsetHeight >=
                           target.scrollHeight - threshold

                        if (isNearBottom && hasMore && !searchValue) {
                           loadMoreOrganizations()
                        }
                     }}
                     popupRender={(menu) => (
                        <div>
                           <div>
                              <SearchField
                                 placeholder="Search organizations"
                                 className="!text-[14px] !bg-transparent !text-white gap-1"
                                 value={searchValue}
                                 onChange={(value) => setSearchValue(value)}
                                 iconClassName='!fill-white/70 scale-75 !px-0 !ml-2'
                              />
                           </div>
                           
                           {menu}
                           
                           {!searchValue && isLoadingMore && (
                              <div className="text-center text-white/70 text-xs py-2">
                                 Loading more...
                              </div>
                           )}
                           {searchValue && filteredOptions.length === 0 && (
                              <div className="text-center py-2 text-white/70 text-xs">
                                 No organizations found
                              </div>
                           )}
                        </div>
                     )}
                  ></Select>
               )}
            </div>

            {/* <img
          src="/images/ff-icon.webp"
          className="mt-16 md:mt-28 w-20 md:w-[98px]"
          alt="ff icon"
        /> */}
            {children}
         </div>
      </>
   )
}

export default OnboardingContainer
